import React, { useState, useEffect, useMemo } from "react";
import {
  Paper,
  Stack,
  Typography,
  Box,
  TextField,
  InputAdornment,
  Skeleton,
  LinearProgress,
  Chip,
  alpha,
  Tooltip,
} from "@mui/material";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import SearchIcon from "@mui/icons-material/Search";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { formatNumber } from "../../constants/ceoDashboard";

// ── Thresholds para colores ─────────────────────────────────────
const GAP_THRESHOLDS = { good: 35, warning: 20 };
const CODING_THRESHOLDS = { good: 15, warning: 5 };

function getGapColor(rate) {
  if (rate >= GAP_THRESHOLDS.good) return { bg: "#D1FAE5", text: "#065F46" };
  if (rate >= GAP_THRESHOLDS.warning) return { bg: "#FEF3C7", text: "#92400E" };
  return { bg: "#FEE2E2", text: "#991B1B" };
}

function getCodingColor(rate) {
  if (rate >= CODING_THRESHOLDS.good) return { bg: "#D1FAE5", text: "#065F46" };
  if (rate >= CODING_THRESHOLDS.warning) return { bg: "#FEF3C7", text: "#92400E" };
  return { bg: "#FEE2E2", text: "#991B1B" };
}

function getOverallScore(gapCompliance, recaptureRate) {
  return Math.round(gapCompliance * 0.6 + recaptureRate * 0.4);
}

function getScoreColor(score) {
  if (score >= 30) return { bg: "#D1FAE5", text: "#065F46" };
  if (score >= 15) return { bg: "#FEF3C7", text: "#92400E" };
  return { bg: "#FEE2E2", text: "#991B1B" };
}

// ── Columnas de la tabla ────────────────────────────────────────
const COLUMNS = [
  { id: "pcpName", label: "Provider", width: "20%", sortable: true },
  { id: "center", label: "Center", width: "10%", sortable: true },
  { id: "memberCount", label: "Members", width: "7%", sortable: true, align: "center" },
  { id: "gapTotal", label: "Total", width: "7%", sortable: true, align: "center", group: "careGaps" },
  { id: "gapClosed", label: "Closed", width: "7%", sortable: true, align: "center", group: "careGaps" },
  { id: "gapOpen", label: "Open", width: "7%", sortable: true, align: "center", group: "careGaps" },
  { id: "gapCompliance", label: "Compliance", width: "10%", sortable: true, align: "center", group: "careGaps" },
  { id: "codingTotal", label: "Total", width: "7%", sortable: true, align: "center", group: "coding" },
  { id: "codingAddressed", label: "Addressed", width: "7%", sortable: true, align: "center", group: "coding" },
  { id: "recaptureRate", label: "Recapture", width: "10%", sortable: true, align: "center", group: "coding" },
  { id: "overallScore", label: "Score", width: "8%", sortable: true, align: "center" },
];

/**
 * Panel - Clinical Performance
 * Evalúa el rendimiento clínico de los PCPs en Care Gaps y Coding Opportunities.
 */
export default function PanelTeamPerformance({ data, loading, loadClinicalPerformance }) {
  const [providers, setProviders] = useState(null);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("overallScore");
  const [sortDir, setSortDir] = useState("desc");

  // ── Cargar datos lazy ─────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    if (loadClinicalPerformance && !providers) {
      setLoadingProviders(true);
      loadClinicalPerformance()
        .then((result) => {
          if (!cancelled) setProviders(result);
        })
        .catch(() => {
          if (!cancelled) setProviders({ providers: [], summary: {} });
        })
        .finally(() => {
          if (!cancelled) setLoadingProviders(false);
        });
    }
    return () => { cancelled = true; };
  }, [loadClinicalPerformance, providers]);

  // ── Sort handler ──────────────────────────────────────────────
  const handleSort = (colId) => {
    if (sortBy === colId) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(colId);
      setSortDir("desc");
    }
  };

  // ── Procesamiento de datos ────────────────────────────────────
  const processedProviders = useMemo(() => {
    const list = providers?.providers || [];
    return list.map((p) => ({
      ...p,
      gapTotal: p.careGaps?.total ?? 0,
      gapClosed: p.careGaps?.closed ?? 0,
      gapOpen: p.careGaps?.open ?? 0,
      gapCompliance: p.careGaps?.complianceRate ?? 0,
      codingTotal: p.coding?.totalOpportunities ?? 0,
      codingAddressed: p.coding?.addressed ?? 0,
      recaptureRate: p.coding?.recaptureRate ?? 0,
      overallScore: getOverallScore(
        p.careGaps?.complianceRate ?? 0,
        p.coding?.recaptureRate ?? 0
      ),
    }));
  }, [providers]);

  // ── Filtrado y sorting ────────────────────────────────────────
  const filteredAndSorted = useMemo(() => {
    let result = processedProviders;

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (p) =>
          p.pcpName?.toLowerCase().includes(term) ||
          p.center?.toLowerCase().includes(term)
      );
    }

    result = [...result].sort((a, b) => {
      const aVal = a[sortBy] ?? 0;
      const bVal = b[sortBy] ?? 0;
      if (typeof aVal === "string") {
        return sortDir === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      return sortDir === "asc" ? aVal - bVal : bVal - aVal;
    });

    return result;
  }, [processedProviders, searchTerm, sortBy, sortDir]);

  const summary = providers?.summary || {};

  // ── Loading skeleton ──────────────────────────────────────────
  if (loading || loadingProviders) {
    return (
      <Paper
        elevation={0}
        sx={{ p: 3, borderRadius: 4, border: "1px solid", borderColor: "divider" }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Skeleton variant="rounded" width={36} height={36} animation="wave" />
          <Skeleton variant="text" width={250} height={32} animation="wave" />
        </Stack>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rectangular" height={80} sx={{ flex: 1, borderRadius: 2 }} animation="wave" />
          ))}
        </Stack>
        {[0, 1, 2, 3, 4].map((i) => (
          <Skeleton key={i} variant="rectangular" height={44} animation="wave" sx={{ borderRadius: 1, mb: 0.5 }} />
        ))}
      </Paper>
    );
  }

  if (!providers) return null;

  return (
    <Paper
      elevation={0}
      sx={{ p: 3, borderRadius: 4, border: "1px solid", borderColor: "divider" }}
    >
      {/* ── Header ─────────────────────────────────────────────────── */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={1.5}
        sx={{ mb: 2.5 }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 36,
              height: 36,
              borderRadius: 2,
              bgcolor: "info.main",
              color: "white",
            }}
          >
            <LocalHospitalIcon fontSize="small" />
          </Box>
          <Typography variant="h6" fontWeight={700}>
            Clinical Performance
          </Typography>
          <Chip
            label={`${formatNumber(summary.totalProviders ?? 0)} providers`}
            size="small"
            variant="outlined"
            sx={{ fontWeight: 600, fontSize: "0.75rem" }}
          />
        </Stack>

        <TextField
          size="small"
          placeholder="Search provider or center..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 240 }}
        />
      </Stack>

      {/* ── Summary Cards ──────────────────────────────────────────── */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
          gap: 2,
          mb: 3,
        }}
      >
        <SummaryCard
          label="Total Open Gaps"
          value={formatNumber(summary.totalOpenGaps ?? 0)}
          sub={`of ${formatNumber(summary.totalGaps ?? 0)} total`}
          color="#DC2626"
        />
        <SummaryCard
          label="Avg Gap Compliance"
          value={`${(summary.avgGapCompliance ?? 0).toFixed(1)}%`}
          sub={`${formatNumber(summary.totalClosedGaps ?? 0)} closed`}
          color={getGapColor(summary.avgGapCompliance ?? 0).text}
        />
        <SummaryCard
          label="Coding Opportunities"
          value={formatNumber(summary.totalCodingOpportunities ?? 0)}
          sub={`${formatNumber(summary.totalCodingAddressed ?? 0)} addressed`}
          color="#7C3AED"
        />
        <SummaryCard
          label="Avg Recapture Rate"
          value={`${(summary.avgRecaptureRate ?? 0).toFixed(1)}%`}
          sub={`${formatNumber(summary.totalProviders ?? 0)} providers evaluated`}
          color={getCodingColor(summary.avgRecaptureRate ?? 0).text}
        />
      </Box>

      {/* ── Table ──────────────────────────────────────────────────── */}
      <Paper
        variant="outlined"
        sx={{ borderRadius: 3, overflow: "hidden" }}
      >
        {/* Table header */}
        <Box sx={{ overflowX: "auto" }}>
          {/* Group headers */}
          <Box
            sx={{
              display: "flex",
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
              borderBottom: "1px solid",
              borderColor: "divider",
              minWidth: 1000,
            }}
          >
            {/* Empty space for Provider + Center + Members */}
            <Box sx={{ width: "37%", minWidth: 370 }} />
            {/* Care Gaps group */}
            <Box
              sx={{
                width: "31%",
                minWidth: 310,
                textAlign: "center",
                py: 0.75,
                borderLeft: "2px solid",
                borderRight: "2px solid",
                borderColor: (theme) => alpha(theme.palette.info.main, 0.2),
                bgcolor: (theme) => alpha(theme.palette.info.main, 0.04),
              }}
            >
              <Typography variant="caption" fontWeight={700} color="info.main">
                CARE GAPS
              </Typography>
            </Box>
            {/* Coding group */}
            <Box
              sx={{
                width: "24%",
                minWidth: 240,
                textAlign: "center",
                py: 0.75,
                borderRight: "2px solid",
                borderColor: (theme) => alpha(theme.palette.secondary.main, 0.2),
                bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.04),
              }}
            >
              <Typography variant="caption" fontWeight={700} color="secondary.main">
                CODING OPPORTUNITIES
              </Typography>
            </Box>
            {/* Score */}
            <Box sx={{ width: "8%", minWidth: 80 }} />
          </Box>

          {/* Column headers */}
          <Box
            sx={{
              display: "flex",
              bgcolor: (theme) => alpha(theme.palette.action.hover, 0.3),
              borderBottom: "2px solid",
              borderColor: "divider",
              minWidth: 1000,
            }}
          >
            {COLUMNS.map((col) => (
              <Box
                key={col.id}
                onClick={() => col.sortable && handleSort(col.id)}
                sx={{
                  width: col.width,
                  px: 1.5,
                  py: 1,
                  cursor: col.sortable ? "pointer" : "default",
                  textAlign: col.align || "left",
                  userSelect: "none",
                  "&:hover": col.sortable ? { bgcolor: "action.hover" } : {},
                  display: "flex",
                  alignItems: "center",
                  justifyContent: col.align === "center" ? "center" : "flex-start",
                  gap: 0.5,
                  ...(col.group === "careGaps" && {
                    borderLeft: col.id === "gapTotal" ? "2px solid" : "none",
                    borderRight: col.id === "gapCompliance" ? "2px solid" : "none",
                    borderColor: (theme) => alpha(theme.palette.info.main, 0.2),
                  }),
                  ...(col.group === "coding" && {
                    borderRight: col.id === "recaptureRate" ? "2px solid" : "none",
                    borderColor: (theme) => alpha(theme.palette.secondary.main, 0.2),
                  }),
                }}
              >
                <Typography variant="caption" fontWeight={700} color="text.secondary" noWrap>
                  {col.label}
                </Typography>
                {col.sortable && sortBy === col.id && (
                  sortDir === "asc"
                    ? <ArrowUpwardIcon sx={{ fontSize: 14, color: "primary.main" }} />
                    : <ArrowDownwardIcon sx={{ fontSize: 14, color: "primary.main" }} />
                )}
              </Box>
            ))}
          </Box>

          {/* Table rows */}
          {filteredAndSorted.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
              No providers found
            </Typography>
          ) : (
            filteredAndSorted.map((p, idx) => {
              const gapColor = getGapColor(p.gapCompliance);
              const codingColor = getCodingColor(p.recaptureRate);
              const scoreColor = getScoreColor(p.overallScore);

              return (
                <Box
                  key={p.pcpName}
                  sx={{
                    display: "flex",
                    minWidth: 1000,
                    borderBottom: idx < filteredAndSorted.length - 1 ? "1px solid" : "none",
                    borderColor: "divider",
                    "&:hover": { bgcolor: "action.hover" },
                    transition: "background-color 150ms ease",
                  }}
                >
                  {/* Provider */}
                  <Box sx={{ width: "20%", px: 1.5, py: 1.25, display: "flex", alignItems: "center" }}>
                    <Typography variant="body2" fontWeight={600} noWrap sx={{ fontSize: "0.8rem" }}>
                      {p.pcpName}
                    </Typography>
                  </Box>

                  {/* Center */}
                  <Box sx={{ width: "10%", px: 1.5, py: 1.25, display: "flex", alignItems: "center" }}>
                    <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: "0.8rem" }}>
                      {p.center}
                    </Typography>
                  </Box>

                  {/* Members */}
                  <Box sx={{ width: "7%", px: 1.5, py: 1.25, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Typography variant="body2" fontWeight={600} sx={{ fontSize: "0.8rem" }}>
                      {p.memberCount}
                    </Typography>
                  </Box>

                  {/* Gap Total */}
                  <CareGapCell width="7%" borderLeft>
                    <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                      {formatNumber(p.gapTotal)}
                    </Typography>
                  </CareGapCell>

                  {/* Gap Closed */}
                  <CareGapCell width="7%">
                    <Typography variant="body2" sx={{ fontSize: "0.8rem", color: "#065F46", fontWeight: 600 }}>
                      {formatNumber(p.gapClosed)}
                    </Typography>
                  </CareGapCell>

                  {/* Gap Open */}
                  <CareGapCell width="7%">
                    <Typography variant="body2" sx={{ fontSize: "0.8rem", color: "#991B1B", fontWeight: 600 }}>
                      {formatNumber(p.gapOpen)}
                    </Typography>
                  </CareGapCell>

                  {/* Gap Compliance */}
                  <CareGapCell width="10%" borderRight>
                    <Tooltip title={`${p.gapClosed} of ${p.gapTotal} gaps closed`} arrow>
                      <Stack spacing={0.25} sx={{ width: "100%" }}>
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          sx={{ color: gapColor.text, textAlign: "center" }}
                        >
                          {p.gapCompliance.toFixed(1)}%
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(p.gapCompliance, 100)}
                          sx={{
                            height: 5,
                            borderRadius: 3,
                            bgcolor: (theme) => alpha(theme.palette.action.hover, 0.4),
                            "& .MuiLinearProgress-bar": {
                              borderRadius: 3,
                              bgcolor: gapColor.text,
                            },
                          }}
                        />
                      </Stack>
                    </Tooltip>
                  </CareGapCell>

                  {/* Coding Total */}
                  <CodingCell width="7%">
                    <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                      {formatNumber(p.codingTotal)}
                    </Typography>
                  </CodingCell>

                  {/* Coding Addressed */}
                  <CodingCell width="7%">
                    <Typography variant="body2" sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#065F46" }}>
                      {formatNumber(p.codingAddressed)}
                    </Typography>
                  </CodingCell>

                  {/* Recapture Rate */}
                  <CodingCell width="10%" borderRight>
                    <Tooltip title={`${p.codingAddressed} of ${p.codingTotal} addressed`} arrow>
                      <Stack spacing={0.25} sx={{ width: "100%" }}>
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          sx={{ color: codingColor.text, textAlign: "center" }}
                        >
                          {p.recaptureRate.toFixed(1)}%
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(p.recaptureRate, 100)}
                          sx={{
                            height: 5,
                            borderRadius: 3,
                            bgcolor: (theme) => alpha(theme.palette.action.hover, 0.4),
                            "& .MuiLinearProgress-bar": {
                              borderRadius: 3,
                              bgcolor: codingColor.text,
                            },
                          }}
                        />
                      </Stack>
                    </Tooltip>
                  </CodingCell>

                  {/* Overall Score */}
                  <Box sx={{ width: "8%", px: 1.5, py: 1.25, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Chip
                      label={p.overallScore}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.75rem",
                        bgcolor: scoreColor.bg,
                        color: scoreColor.text,
                        minWidth: 44,
                      }}
                    />
                  </Box>
                </Box>
              );
            })
          )}
        </Box>
      </Paper>

      {/* ── Footer info ────────────────────────────────────────────── */}
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: "block" }}>
        Score = Care Gap Compliance (60%) + Recapture Rate (40%). Data sourced from Availity and Sante+ systems.
      </Typography>
    </Paper>
  );
}

// ── Subcomponents ─────────────────────────────────────────────────

function SummaryCard({ label, value, sub, color }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        textAlign: "center",
      }}
    >
      <Typography variant="caption" color="text.secondary" fontWeight={600}>
        {label}
      </Typography>
      <Typography variant="h5" fontWeight={800} sx={{ color, my: 0.5 }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {sub}
      </Typography>
    </Paper>
  );
}

function CareGapCell({ children, width, borderLeft, borderRight }) {
  return (
    <Box
      sx={{
        width,
        px: 1.5,
        py: 1.25,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        ...(borderLeft && {
          borderLeft: "2px solid",
          borderColor: (theme) => alpha(theme.palette.info.main, 0.2),
        }),
        ...(borderRight && {
          borderRight: "2px solid",
          borderColor: (theme) => alpha(theme.palette.info.main, 0.2),
        }),
      }}
    >
      {children}
    </Box>
  );
}

function CodingCell({ children, width, borderRight }) {
  return (
    <Box
      sx={{
        width,
        px: 1.5,
        py: 1.25,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        ...(borderRight && {
          borderRight: "2px solid",
          borderColor: (theme) => alpha(theme.palette.secondary.main, 0.2),
        }),
      }}
    >
      {children}
    </Box>
  );
}
